/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 11-05-2026
 *
 * Component voor de linker toolbar-zone (#menulinks) van het canvas.
 * Vervangt de AngularJS #menulinks div (tot golf 4f als Angular 21 Custom Element).
 */
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { BaseIslandComponent } from '../base-island.component';
import { BeginneruitlegDirective } from '../../directives/beginnersuitleg/beginnersuitleg.directive';
import { WeergaveInstellingenService } from '../../services/weergave-instellingen.service';
import { Dashboardv1Service } from '../../services/dashboardv1.service';
import { Dashboardv2Service } from '../../services/dashboardv2.service';
import { SessieService } from '../../services/sessie.service';
import { DlModel } from '../../app/model/dl-model';
import { OpslagService } from '../../app/model/opslag.service';
import { ReplayService } from '../../app/replay/replay.service';
import { ModelbeheerService } from '../../app/model/modelbeheer.service';
import { SamenwerkprotocolService } from '../../app/samenwerking/samenwerkprotocol.service';

/**
 * Hoe lang undo en redo hoogstens wachten op de opslagronde van de vorige actie; daarna gaan ze gewoon
 * door. Dezelfde limiet als bij het afronden van een bewerking voor de simulatie.
 */
const OPSLAGRONDE_WACHTLIMIET_MS = 3000;

@Component({
    selector: 'dl-model-toolbar',
    standalone: true,
    templateUrl: './dl-model-toolbar.component.html',
    styleUrls: ['./dl-model-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TranslatePipe, BeginneruitlegDirective],
})
export class DlModelToolbarComponent extends BaseIslandComponent {

    /**
     * De weergave-instellingen: als de beginnersmodus aan is, worden de title-attributen op de
     * knoppen onderdrukt en toont de beginnersuitleg-directive eigen uitlegballonnen.
     */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De twee docentendashboards (golf 3f en 3g). Allebei sibling-eilanden, dus de knoppen zetten
     * de open-state rechtstreeks in de gedeelde service.
     */
    private readonly dashboardv1 = inject(Dashboardv1Service);
    private readonly dashboardv2 = inject(Dashboardv2Service);

    /**
     * De sessie (golf a1): welke van deze knoppen de gebruiker mag zien — meekijken, de twee
     * docentendashboards en replay — staat in zijn userdata.
     */
    protected readonly sessie = inject(SessieService);

    constructor() {
        super();
    }

    /**
     * Het modeldocument: of er in dit model gewerkt mag worden. Sinds golf a3 eigendom van `DlModel`.
     */
    protected readonly dlModel = inject(DlModel);

    /**
     * De opslagketen (golf a3): de undo-/redo-standen zijn signals, dus de knoppen lezen ze
     * rechtstreeks.
     */
    protected readonly opslag = inject(OpslagService);

    /**
     * De replay (golf a5): de replayknop opent het terugbladeren rechtstreeks op de service.
     */
    protected readonly replayService = inject(ReplayService);

    /**
     * Het modelbeheer (golf 4c): nieuw, laden, opslaan, kopiëren, eigenschappen, samenwerken en
     * meekijken gaan rechtstreeks naar de service.
     */
    protected readonly modelbeheer = inject(ModelbeheerService);

    /**
     * De samenwerk-baas (golf 4d): undo en redo zijn modelacties, en die lopen langs de baas — die kiest
     * tussen zelf uitvoeren en het de leider vragen.
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    // --- Protected methods ---

    /**
     * Zet de vorige stand terug. Eerst de opslagronde van de vorige actie afwachten: die zet de stand pas
     * na de bouwsuggestie-run, en een undo in dat venster zou een verouderde stand op de redo-stapel
     * leggen — de redo bracht de laatste actie dan niet terug (reeks 4d, test 4d.19).
     */
    protected async doeUndo(): Promise<void> {
        await this.samenwerkprotocol.wachtOpOpslagronde(OPSLAGRONDE_WACHTLIMIET_MS);
        this.samenwerkprotocol.doe('undo');
    }

    /**
     * Zet de teruggedraaide stand weer terug; zie `doeUndo` voor het wachten.
     */
    protected async doeRedo(): Promise<void> {
        await this.samenwerkprotocol.wachtOpOpslagronde(OPSLAGRONDE_WACHTLIMIET_MS);
        this.samenwerkprotocol.doe('redo');
    }

    /**
     * Opent het docentendashboard. Geen output: het paneel is een sibling-eiland dat dezelfde
     * root-service leest.
     */
    protected openDashboardv1(): void {
        this.dashboardv1.openen();
    }

    /**
     * Opent het tweede docentendashboard; zelfde route als hierboven.
     */
    protected openDashboardv2(): void {
        this.dashboardv2.openen();
    }
}
